// src/hooks/useNotifications.ts
// ============================================================
// SALT — Notifications Hook
//
// Manages notification state for the navbar bell + panel.
//   - Polls GET /api/notifications/count every 60s for badge
//   - Fetches full notifications on demand (panel open)
//   - Exposes mark-as-read actions
//
// Polling only runs when user is authenticated. Automatically
// stops/starts based on auth state.
// ============================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import axiosClient from '../api/axiosClient';
import type {
  Notification,
  UnreadCountResponse,
  NotificationsResponse,
  NotificationActionResponse,
} from '../types/notification.types';

// ── Configuration ───────────────────────────────────────────

const POLL_INTERVAL_MS = 60_000; // 60 seconds
const NOTIFICATIONS_LIMIT = 20;

// ── Hook ────────────────────────────────────────────────────

interface UseNotificationsOptions {
  /** Whether polling should be active (tie to isAuthenticated) */
  enabled: boolean;
}

interface UseNotificationsReturn {
  /** Current unread count for badge display */
  unreadCount: number;
  /** Loaded notifications (fetched on demand) */
  notifications: Notification[];
  /** Whether notifications are currently being fetched */
  isLoading: boolean;
  /** Fetch/refresh the full notification list */
  fetchNotifications: () => Promise<void>;
  /** Mark a single notification as read */
  markAsRead: (notificationId: number) => Promise<void>;
  /** Mark all notifications as read */
  markAllAsRead: () => Promise<void>;
  /** Delete all notifications for the current user */
  clearAll: () => Promise<void>;
  /** Force-refresh the unread count (e.g. after an action) */
  refreshCount: () => Promise<void>;
}

export function useNotifications({ enabled }: UseNotificationsOptions): UseNotificationsReturn {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Ref to track if component is still mounted
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ── Fetch unread count (lightweight, for polling) ─────────

  const refreshCount = useCallback(async () => {
    if (!enabled) return;

    try {
      const res = await axiosClient.get<{ data: UnreadCountResponse }>('/notifications/count');
      if (mountedRef.current) {
        setUnreadCount(res.data.data.unreadCount);
      }
    } catch (err) {
      // Silently ignore — don't disrupt UX for a badge count failure.
      // 401s are handled by the axiosClient interceptor (auto-redirect).
      console.debug('[useNotifications] count fetch failed:', err);
    }
  }, [enabled]);

  // ── Poll unread count on interval ─────────────────────────

  useEffect(() => {
    if (!enabled) {
      setUnreadCount(0);
      setNotifications([]);
      return;
    }

    // Fetch immediately on mount / auth change
    refreshCount();

    const intervalId = setInterval(refreshCount, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [enabled, refreshCount]);

  // ── Fetch full notification list (on demand) ──────────────

  const fetchNotifications = useCallback(async () => {
    if (!enabled) return;

    setIsLoading(true);
    try {
      const res = await axiosClient.get<{ data: NotificationsResponse }>(
        `/notifications?limit=${NOTIFICATIONS_LIMIT}&offset=0`
      );
      if (mountedRef.current) {
        setNotifications(res.data.data.notifications);
      }
    } catch (err) {
      console.error('[useNotifications] fetch failed:', err);
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [enabled]);

  // ── Mark single notification as read ──────────────────────

  const markAsRead = useCallback(
    async (notificationId: number) => {
      try {
        await axiosClient.patch<NotificationActionResponse>(
          `/notifications/${notificationId}/read`
        );

        if (mountedRef.current) {
          // Optimistic update: mark as read in local state
          setNotifications((prev) =>
            prev.map((n) =>
              n.notificationId === notificationId
                ? { ...n, isRead: true, readAt: new Date().toISOString() }
                : n
            )
          );
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
      } catch (err) {
        console.error('[useNotifications] markAsRead failed:', err);
      }
    },
    []
  );

  // ── Mark all as read ──────────────────────────────────────

  const markAllAsRead = useCallback(async () => {
    try {
      await axiosClient.patch<NotificationActionResponse>('/notifications/read-all');

      if (mountedRef.current) {
        setNotifications((prev) =>
          prev.map((n) => ({
            ...n,
            isRead: true,
            readAt: n.readAt || new Date().toISOString(),
          }))
        );
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('[useNotifications] markAllAsRead failed:', err);
    }
  }, []);

  // ── Clear all (delete all notifications) ────────────────

  const clearAll = useCallback(async () => {
    try {
      await axiosClient.delete('/notifications/clear');

      if (mountedRef.current) {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('[useNotifications] clearAll failed:', err);
    }
  }, []);

  return {
    unreadCount,
    notifications,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearAll,
    refreshCount,
  };
}